// src/pages/PlayableGames.js
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PlayableGameCard from "../components/PlayableGameCard";
import { useAllGames } from "../hooks/useAllGames";
import GamesLoader from "../components/GamesLoader";

const PAGE_SIZE = 100;

export default function PlayableGames() {
  const navigate = useNavigate();
  const { games, loading } = useAllGames();
    const [uiLoading, setUiLoading] = useState(true);

  const [query, setQuery] = useState("");
  const [genre, setGenre] = useState("All");
  const [tag, setTag] = useState("All");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  
    const listRef = useRef(null);

  // Only games that have a webglPath are playable on-site
  const playableGames = useMemo(
    () =>
      games.filter(
        (g) => g.webglPath && String(g.webglPath).trim() !== ""
      ),
    [games]
  );

  const genres = useMemo(() => {
    const set = new Set();
    playableGames.forEach((g) => g.genre && set.add(g.genre));
    return ["All", ...Array.from(set).sort()];
  }, [playableGames]);

  const tags = useMemo(() => {
    const set = new Set();
    playableGames.forEach((g) => (g.tags || []).forEach((t) => set.add(t)));
    return ["All", ...Array.from(set).sort()];
  }, [playableGames]);

  const results = useMemo(() => {
    let list = [...playableGames];

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((g) => {
        const haystack = [
          g.title,
          g.description,
          g.genre,
          ...(g.tags || []),
          g.source,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    if (genre !== "All") {
      list = list.filter((g) => g.genre === genre);
    }

    if (tag !== "All") {
      list = list.filter((g) => (g.tags || []).includes(tag));
    }

    // Kepithor first, then others
    list.sort((a, b) => {
      const aIsK = (a.source || "").toLowerCase() === "kepithor";
      const bIsK = (b.source || "").toLowerCase() === "kepithor";
      if (aIsK && !bIsK) return -1;
      if (bIsK && !aIsK) return 1;
      return a.title.localeCompare(b.title);
    });

    return list;
  }, [playableGames, query, genre, tag]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query, genre, tag]);

  const visibleResults = useMemo(
    () => results.slice(0, visibleCount),
    [results, visibleCount]
  );

  const canShowMore = visibleCount < results.length;

  
  
  useEffect(() => {
    // If data is still loading from the hook, keep UI loader on
    if (loading) {
      setUiLoading(true);
      return;
    }
  
    // When results change (new filters/search/etc), show loader briefly
    setUiLoading(true);
    const t = setTimeout(() => {
      setUiLoading(false);
    }, 200); // 200ms feels snappy; you can tweak
  
    return () => clearTimeout(t);
  }, [loading, results.length]);
  

  return (
    <div ref={listRef} className="grid gap-6 space-y-3 overflow-y-auto max-h-[calc(100vh-200px)]">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">
          Playable on Kepithor
        </h1>
        <p className="text-sm text-slate-300 mt-1">
          Games you can play instantly in your browser, including Kepithor
          titles and partner games.
        </p>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-slate-900/60 border border-slate-800 rounded-2xl px-4 py-3 shadow-sm shadow-emerald-500/10">
        <input
          placeholder="Search by title, description, or source…"
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 min-w-[240px] text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
        >
          {genres.map((g) => (
            <option key={g} value={g}>
              {g === "All" ? "All genres" : g}
            </option>
          ))}
        </select>
{/* 
        <select
          className="border border-slate-700 bg-slate-900/80 rounded-xl px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        >
          {tags.map((t) => (
            <option key={t} value={t}>
              {t === "All" ? "All tags" : t}
            </option>
          ))}
        </select> */}
      </div>

      {/* Results */}
            {uiLoading  ? (
               <GamesLoader />
            ) : (
        <>
          <div className="space-y-3">
            {visibleResults.map((game) => (
              <PlayableGameCard
                key={game.id}
                game={game}
                onOpen={() => navigate(`/game/${game.id}`)}
                onPlay={() => navigate(`/play/${game.id}`)}
              />
            ))}
          </div>

          {canShowMore && (
  <div className="mt-4 flex justify-center gap-3">
    
    {/* Show More */}
    <button
      type="button"
      onClick={() => setVisibleCount(prev => prev + PAGE_SIZE)}
      className="px-4 py-2 text-sm rounded-xl border border-emerald-500 text-emerald-300 hover:bg-emerald-500/10"
    >
      Show more games ({results.length - visibleCount} remaining)
    </button>


 {/* Back to Top */}
<button
  type="button"
  onClick={() => {
    if (listRef.current) {
      listRef.current.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  }}
  className="px-4 py-2 text-sm rounded-xl border border-slate-600 text-slate-300 hover:bg-slate-600/20"
>
  Back to Top ↑
</button>
  </div>
)}

        </>
      )}
    </div>
  );
}
